import { useEffect, useState } from "react";
import { api, formatApiError } from "./api";
import { toast } from "sonner";

/**
 * Fetches the plan catalog + current subscription + Razorpay config,
 * and exposes a `buyPlan(planId)` helper that opens the checkout.
 */
export function useBilling() {
  const [plansData, setPlansData] = useState(null);
  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState({ enabled: false });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/payment/plans"),
      api.get("/subscription"),
      api.get("/payment/razorpay/config"),
    ])
      .then(([p, s, c]) => {
        if (!alive) return;
        setPlansData(p.data);
        setSub(s.data);
        setConfig(c.data);
      })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, []);

  const buyPlan = async (planId) => {
    if (!config.enabled) return toast.error("Razorpay not configured");
    if (!window.Razorpay) return toast.error("Payment SDK not loaded yet");
    setBusy(planId);
    try {
      const { data: order } = await api.post("/payment/razorpay/create-order", { plan: planId });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "MenuMojo",
        description: order.label,
        order_id: order.order_id,
        theme: { color: "#FF6500" },
        handler: async (resp) => {
          try {
            await api.post("/payment/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Subscription activated! 🎉");
            setTimeout(() => window.location.reload(), 800);
          } catch (e) {
            toast.error(formatApiError(e));
            setBusy(null);
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      });
      // Surface payment failures back to the user — without this, a failed test card
      // just silently closes the modal and users think "nothing happened".
      rzp.on("payment.failed", (resp) => {
        const err = resp?.error || {};
        const msg = err.description || err.reason || err.code || "Payment failed";
        toast.error(`Payment failed: ${msg}`);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  return { plansData, sub, config, busy, buyPlan };
}
